/**
 * Service functions for handling tactical changes in matches
 */

import { buildLiveCommandAttemptData, pauseGame, resumeGame } from './simulationControlService';

function getApiErrorMessage(error, fallback) {
  return error?.response?.data?.message
    || error?.response?.data?.error
    || error?.message
    || fallback;
}

function createTacticChangeResult({
  tacticChanged,
  resumeAttempted = false,
  resumeSucceeded = null
}) {
  return {
    tacticChanged,
    resumeAttempted,
    resumeSucceeded
  };
}

function getSocketErrorDetails(error) {
  return error?.response || (error?.code ? error : undefined);
}

function notifyError(onError, message, details) {
  if (details !== undefined) {
    onError?.(message, details);
    return;
  }
  onError?.(message);
}

/**
 * Handles tactical changes for both scheduled and in-progress matches
 * 
 * @param {Object} params Configuration parameters
 * @param {string} params.tacticId The ID of the new tactic
 * @param {string} params.formationId The ID of the new formation
 * @param {string} params.managedTeamId The ID of the team being managed
 * @param {string} params.userTeamSide Either 'home' or 'away'
 * @param {string} params.matchStatus The status of the match ('scheduled', 'in_progress', etc.)
 * @param {string} [params.engineBootAttemptId] Current live command attempt marker
 * @param {Object} params.socketCtx The socket context for in-progress matches
 * @param {Function} params.saveTacticAPI API function to save tactic for scheduled matches
 * @param {Function} params.onSuccess Callback for successful tactic change
 * @param {Function} params.onError Callback for failed tactic change
 * @param {Function} params.onResumeError Callback for failed resume after a successful tactic change
 * @param {boolean} params.pauseBeforeChange Whether to pause the game before making changes (default: true)
 * @param {boolean} params.resumeAfterChange Whether to resume the game after changes (default: true)
 * @returns {Promise<Object>} Tactic change result
 */
export async function handleTacticChange({
  tacticId,
  formationId,
  managedTeamId,
  userTeamSide,
  matchStatus,
  matchId, // <-- NEW PARAM: needed for backend validation
  engineBootAttemptId,
  socketCtx,
  saveTacticAPI,
  onSuccess,
  onError,
  onResumeError,
  pauseBeforeChange = true,
  resumeAfterChange = true
}) {
  console.log("%%%%%% [tacticalService] handleTacticChange FUNCTION ENTERED %%%%%%", { tacticId, formationId, managedTeamId, matchStatus, hasSocketCtx: !!socketCtx, hasSaveTacticAPI: !!saveTacticAPI });

  try {
    // Validate required parameters
    if (!managedTeamId) {
      console.error('[tacticalService] Missing managedTeamId');
      onError?.("Cannot change tactic: Managed team ID missing.");
      return createTacticChangeResult({ tacticChanged: false });
    }

    const isInProgressMatch =
      matchStatus === 'inProgress'
      || matchStatus === 'in_progress'
      || matchStatus === 'inprogress';

    // Different handling based on match status
    if (isInProgressMatch) {
      console.log('[tacticalService] Match is inProgress, using WebSocket.');
      // For in-progress matches, send WebSocket command
      if (!socketCtx || !socketCtx.sendUserCommand) {
        console.error('[tacticalService] Socket context or sendUserCommand missing for inProgress match.');
        onError?.("WebSocket connection not available. Cannot change tactic.");
        return createTacticChangeResult({ tacticChanged: false });
      }

      if (!matchId) {
        console.error('[tacticalService] Missing matchId for in-progress match.');
        onError?.("Cannot change tactic: Match ID missing.");
        return createTacticChangeResult({ tacticChanged: false });
      }

      // Pause game before making changes if requested
      if (pauseBeforeChange) {
        const pauseSuccess = await pauseGame({
          matchId,
          reason: 'tactic_change',
          engineBootAttemptId,
          socketCtx,
          onSuccess: (message) => onSuccess?.(`${message}. Changing tactics...`),
          onError
        });
        
        if (!pauseSuccess) {
          return createTacticChangeResult({ tacticChanged: false });
        }
      }
      
      // Prepare tactic change command payload (camel-case + matchId)
      const commandDataForBackend = {
        matchId: matchId?.toString(), // REQUIRED by backend validator
        teamId: userTeamSide,         // camel-case key
        tacticId: tacticId?.toString(),
        formation: formationId?.toString(),
        ...buildLiveCommandAttemptData(engineBootAttemptId)
      };

      const finalPayloadForSocket = {
        command_type: 'TACTIC_CHANGE',
        command_data: commandDataForBackend
      };
      
      console.log('[tacticalService] WebSocket command summary:', {
        commandType: finalPayloadForSocket.command_type,
        matchIdPresent: Boolean(commandDataForBackend.matchId),
        teamSide: commandDataForBackend.teamId,
        commandDataKeyCount: Object.keys(commandDataForBackend).length,
        clientAttemptSupplied: Boolean(commandDataForBackend.engineBootAttemptId)
      });
      
      // Send command via WebSocket
      try {
        await socketCtx.sendUserCommand(finalPayloadForSocket);

        onSuccess?.('Tactic change request sent!');

        // Resume game after successful change if requested
        if (resumeAfterChange) {
          const resumeSuccess = await resumeGame({
            matchId,
            engineBootAttemptId,
            socketCtx,
            onSuccess,
            onError: (message, details) => {
              console.error('[tacticalService] Failed to resume after tactic change:', message);
              notifyError(onResumeError, message, details);
            }
          });

          if (!resumeSuccess) {
            return createTacticChangeResult({
              tacticChanged: true,
              resumeAttempted: true,
              resumeSucceeded: false
            });
          }

          return createTacticChangeResult({
            tacticChanged: true,
            resumeAttempted: true,
            resumeSucceeded: true
          });
        }

        return createTacticChangeResult({ tacticChanged: true });
      } catch (error) {
        console.error('[tacticalService] Failed to send tactic change request:', error);
        const errorMessage = error?.message || 'Failed to process tactic change request.';
        const errorDetails = getSocketErrorDetails(error);

        // If tactic change failed but we paused the game, we should resume it
        if (pauseBeforeChange && resumeAfterChange) {
          await resumeGame({
            matchId,
            engineBootAttemptId,
            socketCtx,
            onSuccess: () => {},
            onError: (resumeError) => console.error('Failed to resume after tactic change error:', resumeError)
          });
        }

        notifyError(onError, errorMessage, errorDetails);
        return createTacticChangeResult({ tacticChanged: false });
      }
    } else {
      console.log(`[tacticalService] Match status is ${matchStatus}, using API.`);
      // For scheduled matches, use the API
      if (!saveTacticAPI) {
        console.error('[tacticalService] saveTacticAPI missing for scheduled match.');
        onError?.("API method for saving tactics is not available.");
        return createTacticChangeResult({ tacticChanged: false });
      }

      // Save tactic using API
      console.log('[tacticalService] Calling saveTacticAPI with:', { teamId: managedTeamId, tacticId, formation: formationId });
      await saveTacticAPI({
        teamId: managedTeamId,
        tacticId: tacticId,
        formation: formationId, 
      });
      
      onSuccess?.('Tactic updated successfully');
      return createTacticChangeResult({ tacticChanged: true });
    }
  } catch (error) {
    console.error('[tacticalService] Error in handleTacticChange:', error);
    onError?.(getApiErrorMessage(error, 'Unknown error changing tactic'));
    // Ensure game is resumed if it was paused and an unexpected error occurs
    if ((matchStatus === 'inProgress' || matchStatus === 'in_progress' || matchStatus === 'inprogress') && pauseBeforeChange && resumeAfterChange && socketCtx) {
        console.warn('[tacticalService] Resuming game due to unexpected error after pause.');
        await resumeGame({ matchId, engineBootAttemptId, socketCtx, onSuccess: () => {}, onError: () => {} });
    }
    return createTacticChangeResult({ tacticChanged: false });
  }
} 
